import { Directive, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Cursor spotlight: a soft radial highlight follows the pointer across the element. Adds the
 * `.has-spot` hook (the glow itself lives in global CSS, scoped to card classes) and updates
 * `--spot-x` / `--spot-y` on pointer move. Pointer-driven DOM writes only (zoneless-safe);
 * skipped on the server and on touch devices.
 */
@Directive({ selector: '[appSpotlight]' })
export class SpotlightDirective implements OnInit, OnDestroy {
  private readonly el: HTMLElement;
  private active = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, ref: ElementRef<HTMLElement>) {
    this.el = ref.nativeElement;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (matchMedia('(hover: none)').matches) return;

    this.active = true;
    this.el.classList.add('has-spot');
    this.el.addEventListener('pointerenter', this.onEnter);
    this.el.addEventListener('pointermove', this.onMove);
    this.el.addEventListener('pointerleave', this.onLeave);
  }

  ngOnDestroy(): void {
    if (!this.active) return;
    this.el.removeEventListener('pointerenter', this.onEnter);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerleave', this.onLeave);
  }

  private onEnter = (): void => {
    this.el.classList.add('spot-on');
  };

  private onMove = (e: PointerEvent): void => {
    const r = this.el.getBoundingClientRect();
    this.el.style.setProperty('--spot-x', `${e.clientX - r.left}px`);
    this.el.style.setProperty('--spot-y', `${e.clientY - r.top}px`);
  };

  private onLeave = (): void => {
    this.el.classList.remove('spot-on');
  };
}
